import React from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Brain, Cpu, Sparkles, Target, Rocket, Shield } from 'lucide-react';

const AIConsultingPage = () => {
  const services = [
    {
      icon: <Brain className="w-8 h-8" />,
      title: "AI Strategy & Roadmap",
      description: "Develop comprehensive AI strategies aligned with your business objectives. We assess your current capabilities, identify opportunities, and create actionable roadmaps for AI adoption.",
      benefits: ["AI readiness assessment", "Strategic planning", "ROI analysis"]
    },
    {
      icon: <Cpu className="w-8 h-8" />,
      title: "Custom AI Solutions",
      description: "Build tailored AI solutions that address your specific business challenges. From natural language processing to computer vision, we develop and deploy custom AI models.",
      benefits: ["Custom model development", "API integration", "Model training"]
    },
    {
      icon: <Sparkles className="w-8 h-8" />,
      title: "Generative AI Integration",
      description: "Leverage the power of generative AI including ChatGPT, Claude, and other LLMs. We help you integrate AI assistants, content generation, and automation into your workflows.",
      benefits: ["LLM integration", "Prompt engineering", "AI automation"]
    },
    {
      icon: <Target className="w-8 h-8" />,
      title: "AI-Powered Analytics",
      description: "Enhance your analytics capabilities with AI-driven insights. Implement predictive analytics, anomaly detection, and automated reporting to stay ahead of trends.",
      benefits: ["Predictive analytics", "Anomaly detection", "Smart forecasting"]
    },
    {
      icon: <Rocket className="w-8 h-8" />,
      title: "AI Implementation",
      description: "End-to-end implementation services from proof of concept to production deployment. We ensure smooth integration with your existing systems and provide training for your team.",
      benefits: ["POC development", "Production deployment", "Team training"]
    },
    {
      icon: <Shield className="w-8 h-8" />,
      title: "Responsible AI",
      description: "Implement AI solutions with ethics, fairness, and transparency at the core. We ensure your AI systems are compliant, unbiased, and aligned with responsible AI principles.",
      benefits: ["Ethics framework", "Bias detection", "Compliance assurance"]
    }
  ];

  const useCases = [
    {
      title: "Customer Service Automation",
      description: "AI-powered chatbots and virtual assistants that provide 24/7 customer support",
      icon: "💬"
    },
    {
      title: "Intelligent Process Automation",
      description: "Automate repetitive tasks and workflows with AI-driven RPA solutions",
      icon: "⚙️"
    },
    {
      title: "Personalization Engines",
      description: "Deliver personalized experiences with AI-powered recommendation systems",
      icon: "🎯"
    },
    {
      title: "Fraud Detection",
      description: "Real-time fraud detection using advanced machine learning algorithms",
      icon: "🛡️"
    },
    {
      title: "Content Generation",
      description: "Automated content creation for marketing, documentation, and more",
      icon: "✍️"
    },
    {
      title: "Predictive Maintenance",
      description: "Prevent equipment failures with AI-powered predictive maintenance",
      icon: "🔧"
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      
      {/* Hero Section */}
      <div className="relative bg-gradient-to-br from-[#0d6571] to-[#0d6571]/80 py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-center gap-4 mb-6">
            <div className="p-4 bg-white/10 rounded-xl">
              <Brain className="w-12 h-12 text-white" />
            </div>
            <div>
              <h1 className="text-5xl font-bold text-white mb-2">
                AI Consulting Services
              </h1>
              <p className="text-xl text-white/90">
                Empower your business with artificial intelligence
              </p>
            </div>
          </div>
          <p className="text-lg text-white/80 max-w-3xl">
            Navigate the AI revolution with confidence. Our expert consultants help you identify, 
            develop, and deploy AI solutions that drive real business value and competitive advantage.
          </p>
        </div>
      </div>

      {/* Services Grid */}
      <div className="py-16">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Our AI Consulting Services
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Comprehensive AI solutions from strategy to implementation
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <div
                key={index}
                className="bg-white rounded-xl shadow-lg hover:shadow-2xl transition-all p-8 border-t-4 border-[#0d6571]"
              >
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#0d6571]/10 text-[#0d6571] mb-6">
                  {service.icon}
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">
                  {service.title}
                </h3>
                <p className="text-gray-700 leading-relaxed mb-6">
                  {service.description}
                </p>
                <div className="space-y-2">
                  {service.benefits.map((benefit, idx) => (
                    <div key={idx} className="flex items-center gap-2 text-gray-600">
                      <div className="w-1.5 h-1.5 rounded-full bg-[#0d6571]"></div>
                      <span className="text-sm">{benefit}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Use Cases Section */}
      <div className="bg-white py-16">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              AI Use Cases
            </h2>
            <p className="text-lg text-gray-600">
              Real-world applications of artificial intelligence
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {useCases.map((useCase, index) => (
              <div
                key={index}
                className="bg-gradient-to-br from-gray-50 to-white p-6 rounded-xl border border-gray-200 hover:border-[#0d6571] transition-colors"
              >
                <div className="text-4xl mb-4">{useCase.icon}</div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  {useCase.title}
                </h3>
                <p className="text-gray-600">
                  {useCase.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Why Choose Us */}
      <div className="bg-gray-50 py-16">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                Why Choose FXC Management Services for AI?
              </h2>
              <div className="space-y-6">
                <div className="flex gap-4">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                      <Brain className="w-6 h-6 text-blue-600" />
                    </div>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Expert Team</h3>
                    <p className="text-gray-600">
                      Our team includes AI researchers, data scientists, and ML engineers with 
                      proven track records in delivering enterprise AI solutions.
                    </p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                      <Target className="w-6 h-6 text-green-600" />
                    </div>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Business-First Approach</h3>
                    <p className="text-gray-600">
                      We focus on solving real business problems, not just implementing technology. 
                      Every AI solution is designed to deliver measurable ROI.
                    </p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center">
                      <Sparkles className="w-6 h-6 text-purple-600" />
                    </div>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Cutting-Edge Technology</h3>
                    <p className="text-gray-600">
                      We stay at the forefront of AI innovation, leveraging the latest models, 
                      frameworks, and best practices from leaders like OpenAI, Google, and Meta.
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div className="bg-gradient-to-br from-[#0d6571] to-[#0d6571]/80 rounded-2xl p-12 text-white">
              <h3 className="text-3xl font-bold mb-6">Ready to Embrace AI?</h3>
              <p className="text-white/90 mb-8 text-lg">
                Start your AI journey with a free consultation. Let's explore how artificial 
                intelligence can transform your business operations and drive growth.
              </p>
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 rounded-full bg-white"></div>
                  <span>Free initial consultation</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 rounded-full bg-white"></div>
                  <span>Custom AI roadmap</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 rounded-full bg-white"></div>
                  <span>POC development</span>
                </div>
              </div>
              <a
                href="/contact"
                className="inline-block mt-8 px-8 py-4 bg-white text-[#0d6571] rounded-full hover:bg-gray-100 font-bold transition-colors"
              >
                Schedule Consultation
              </a>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default AIConsultingPage;
